import { handleAfterCallWork, changeAgentStateToAvailable } from "./agent";
import {
  handleAcceptedVoice,
  handleConnectedVoice,
  handleEndedVoice,
  handleIncomingVoice,
  handleRefreshVoice,
  handleMissedVoice,
  handleDestroyVoice,
  updateCallAndSend,
  handleConnectingVoice,
} from "./call";
import { CONTACT_EVENT_STATE_MAP } from "../../constants/constants";
import {
  handleAcceptedTask,
  handleConnectedTask,
  handleConnectingTask,
  handleEndedTask,
  handleIncomingTask,
  handleRefreshTask,
  updateTaskAndSend,
  handleMissedTask,
  handleDestroyTask,
} from "./task";
import {
  handleAcceptedChat,
  handleConnectedChat,
  handleConnectingChat,
  handleDestroyChat,
  handleEndedChat,
  handleIncomingChat,
  handleMissedChat,
  handleRefreshChat,
  updateChatAndSend,
} from "./chat";
import { formatCaseAttributes } from "../../utils";
import {
  handleAcceptedVoicemail,
  handleConnectedVoicemail,
  handleConnectingVoicemail,
  handleDestroyVoicemail,
  handleEndedVoicemail,
  handleIncomingVoicemail,
  handleMissedVoicemail,
  handleRefreshVoicemail,
  updateVoicemailAndSend,
} from "./voicemail";
import {
  handleAcceptedCallback,
  handleConnectedCallback,
  handleConnectingCallback,
  handleDestroyCallback,
  handleEndedCallback,
  handleIncomingCallback,
  handleMissedCallback,
  handleRefreshCallback,
  updateCallbackAndSend,
} from "./callback";
import Emitter from "../../emitter";
import { STATE_AGENT_STATE_AVAILABLE_NOTIFICATION } from "../constants";
import { connectSession } from "./session";

const LOG_PREFIX = "[ContactHandler]";

// Centralized missed contact tracking
interface MissedContactEntry {
  contactId: string;
  missedCount: number;
  lastMissedAt: number;
  contactType: string;
}

const missedContactTracker = new Map<string, MissedContactEntry>();

/**
 * Gets a unique key for tracking based on the customer/phone number
 * This allows us to track if the SAME customer calls back and is missed again
 */
function getContactKey(contact: any): string {
  // Try to get a unique identifier for the customer (phone number, customer ID, etc.)
  const attributes = contact.getAttributes();
  const phoneNumber = contact.getActiveInitialConnection()?.getEndpoint()?.phoneNumber;
  const customerPhone = attributes?.Customer_ANI || attributes?.Customer_Phone;
  
  // Use phone number as the key to track the same customer across different contact instances
  const customerKey = phoneNumber || customerPhone || contact.getContactId();
  
  return `customer_${customerKey}`;
}

/**
 * Tracks a missed contact and changes agent state if same contact is missed twice
 */
function trackMissedContact(contact: any): void {
  const contactKey = getContactKey(contact);
  const contactId = contact.getContactId();
  const contactType = contact.getType();
  
  console.log(`${LOG_PREFIX} Tracking missed contact: ${contactId} (${contactType}), key: ${contactKey}`);
  
  // Get existing entry or create new one
  let entry = missedContactTracker.get(contactKey);
  
  if (entry) {
    // Same customer/contact missed again - increment count
    entry.missedCount++;
    entry.lastMissedAt = Date.now();
    console.log(`${LOG_PREFIX} Same customer missed again. Miss count: ${entry.missedCount}`);
  } else {
    // First time this customer/contact is missed
    entry = {
      contactId,
      missedCount: 1,
      lastMissedAt: Date.now(),
      contactType,
    };
    console.log(`${LOG_PREFIX} First miss for this customer. Miss count: 1`);
  }
  
  missedContactTracker.set(contactKey, entry);
  console.log(`${LOG_PREFIX} Total unique customers with missed contacts: ${missedContactTracker.size}`);
  
  // Get the threshold from config
  const threshold = connectSession.missedContactThreshold;
  console.log(`${LOG_PREFIX} Using missed contact threshold: ${threshold}`);
  
  // Check if THIS specific contact has been missed enough times
  if (entry.missedCount >= threshold) {
    console.log(
      `${LOG_PREFIX} Same customer missed ${entry.missedCount} times (threshold: ${threshold}). Changing agent state to Not Ready.`
    );
    
    // Change agent state to Available (which maps to "Not Ready" in the backend)
    changeAgentStateToAvailable();
    
    // Emit notification that agent state was changed due to missed contacts
    setTimeout(() => {
      Emitter.emit(STATE_AGENT_STATE_AVAILABLE_NOTIFICATION, {
        type: "MISSED_CONTACT_NOTIFICATION",
        body: `Agent state changed to Not Ready after missing the same contact ${entry.missedCount} times.`,
      });
    }, 100);
    
    // Clear the tracker after changing state
    missedContactTracker.clear();
  }
}

/**
 * Resets the missed contact tracking when agent accepts a contact
 * Clears ALL tracking to give agent a fresh start
 */
function resetMissedContact(_contact: any): void {
  console.log(`${LOG_PREFIX} Agent accepted a contact. Clearing all missed contact tracking.`);
  
  // Clear all tracking when agent successfully accepts any contact
  // This gives them a fresh start
  missedContactTracker.clear();
}

function handleContactError(error: Error) {
  console.log("Error!!!!!!!!: contact error", error);
}

function registerCallHandlers(contact: any) {
  contact.onIncoming(handleIncomingVoice);
  contact.onConnecting(handleConnectingVoice);
  contact.onAccepted(handleAcceptedVoice);
  contact.onConnected(handleConnectedVoice);
  contact.onEnded(handleEndedVoice);
  contact.onACW(handleAfterCallWork);
  contact.onRefresh(handleRefreshVoice);
  contact.onMissed(handleMissedVoice);
  contact.onDestroy(handleDestroyVoice);
  contact.onError(handleContactError);
}

function registerChatHandlers(contact: any) {
  contact.onIncoming(handleIncomingChat);
  contact.onConnecting(handleConnectingChat);
  contact.onAccepted(handleAcceptedChat);
  contact.onConnected(handleConnectedChat);
  contact.onEnded(handleEndedChat);
  // contact.onACW(handleAfterCallWork);
  contact.onRefresh(handleRefreshChat);
  contact.onMissed(handleMissedChat);
  contact.onDestroy(handleDestroyChat);
  contact.onError(handleContactError);
}

function registerTaskHandlers(contact: any) {
  const taskAttributes = formatCaseAttributes(contact.getAttributes());
  if (taskAttributes.TASK_TYPE === "VOICEMAIL") {
    registerVoicemailHandlers(contact);
  } else if (taskAttributes.TASK_TYPE === "CALLBACK") {
    registerCallbackHandlers(contact);
  } else {
    updateTaskAndSend(CONTACT_EVENT_STATE_MAP.INITIAL, contact);
    contact.onIncoming(handleIncomingTask);
    contact.onConnecting(handleConnectingTask);
    contact.onAccepted(handleAcceptedTask);
    contact.onConnected(handleConnectedTask);
    contact.onEnded(handleEndedTask);
    // contact.onACW(handleAfterCallWork);
    contact.onRefresh(handleRefreshTask);
    contact.onMissed(handleMissedTask);
    contact.onDestroy(handleDestroyTask);
    contact.onError(handleContactError);
  }
}

function registerCallbackHandlers(contact: any) {
  updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.INITIAL, contact);
  contact.onIncoming(handleIncomingCallback);
  contact.onConnecting(handleConnectingCallback);
  contact.onAccepted(handleAcceptedCallback);
  contact.onConnected(handleConnectedCallback);
  contact.onEnded(handleEndedCallback);
  contact.onRefresh(handleRefreshCallback);
  contact.onMissed(handleMissedCallback);
  contact.onDestroy(handleDestroyCallback);
  contact.onError(handleContactError);
}

function registerVoicemailHandlers(contact: any) {
  updateVoicemailAndSend(CONTACT_EVENT_STATE_MAP.INITIAL, contact);
  contact.onIncoming(handleIncomingVoicemail);
  contact.onConnecting(handleConnectingVoicemail);
  contact.onAccepted(handleAcceptedVoicemail);
  contact.onConnected(handleConnectedVoicemail);
  contact.onEnded(handleEndedVoicemail);
  contact.onRefresh(handleRefreshVoicemail);
  contact.onMissed(handleMissedVoicemail);
  contact.onDestroy(handleDestroyVoicemail);
  contact.onError(handleContactError);
}

/**
 *
 * @param contact function to send initial state of contact to AIC Server
 */
function sendInitialContactAPI(contact: any) {
  const prefix = `sendInitialContactAPI: contact id =  ${
    contact.contactId || contact.getContactId()
  }`;
  // sendMessage({
  //     type: 'notif',
  //     chan: STATE_INITIAL_CONTACT,
  //     body: {contactId: contact.contactId || contact.getContactId()}
  // });
  console.log(prefix + "Initial Contact API sent");
}

function registerMissedContactTracker(contact: any) {
  contact.onMissed((c: any) => {
    trackMissedContact(c);
  });
  contact.onConnected((c: any) => {
    resetMissedContact(c);
  });
}
/**
 *
 * @param contact function to subscribe to contact events
 */
export function subscribeToContactEvents(contact: any) {
  try {
    if (!contact) {
      console.log("No contact provided to subscribeToContactEvents");
      return;
    }
    // Track missed contact for automatic state change after 2 consecutive misses

    // For voice calls we need to track only inbound calls
    if (contact.getType() === connect.ContactType.VOICE && !contact.isInbound()) {
       // We don't need to track missed contacts for outbound calls
    } else {
      registerMissedContactTracker(contact);
    }

    // Subscribing to Streams API Long Polling Contact events
    sendInitialContactAPI(contact);

    switch (contact.getType()) {
      case connect.ContactType.VOICE:
        //This is for when call was there before login or during initialization
        if (
          contact.getActiveInitialConnection() &&
          contact.getActiveInitialConnection().getEndpoint()
        ) {
          console.log(
            "New contact is from: " +
              contact.getActiveInitialConnection().getEndpoint().phoneNumber,
            contact.getActiveInitialConnection()
          );
          updateCallAndSend(CONTACT_EVENT_STATE_MAP.INITIAL, contact);
        }
        registerCallHandlers(contact);
        break;
      case connect.ContactType.CHAT:
        try {
          if (
            contact.getActiveInitialConnection() &&
            contact.getActiveInitialConnection().getEndpoint()
          ) {
            updateChatAndSend(CONTACT_EVENT_STATE_MAP.INITIAL, contact);
          }
        } catch (error) {
          console.log("Error in chat type contact", error);
        }
        registerChatHandlers(contact);
        break;
      case connect.ContactType.TASK:
        try {
          if (
            contact.getActiveInitialConnection() &&
            contact.getActiveInitialConnection().getEndpoint()
          ) {
            registerTaskHandlers(contact);
          }
        } catch (error) {
          console.log("Error in TASK type contact", error);
        }
        break;
      default:
        console.log(" subscribeToContactEvents: Inside default!!");
        break;
    }
  } catch (error) {
    console.log("Error in subscribeToContactEvents:", error);
  }
}
